import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, XCircle, Clock, Plus, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const IntegrationsPage = () => {
  const [providers, setProviders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load available providers
      const providersRes = await axios.get(`${API_BASE_URL}/api-wizard/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(providersRes.data.providers || []);

      // Load configured integrations
      const integrationsRes = await axios.get(`${API_BASE_URL}/api-wizard/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(integrationsRes.data.integrations || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvider) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');

      await axios.post(
        `${API_BASE_URL}/api-wizard/integrations`,
        {
          provider: selectedProvider.id,
          credentials: credentials,
          description: `${selectedProvider.name} integration`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Integration submitted for approval!');
      setCredentials({});
      setSelectedProvider(null);
      loadData();
    } catch (error) {
      console.error('Error submitting integration:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit integration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async (integrationId) => {
    try {
      setTesting(true);
      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `${API_BASE_URL}/api-wizard/integrations/${integrationId}/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error('Connection test failed');
      }
      loadData();
    } catch (error) {
      console.error('Error testing integration:', error);
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(
        `${API_BASE_URL}/api-wizard/integrations/${integrationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Integration deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      active: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', text: 'Active' },
      failed: { icon: XCircle, color: 'bg-red-100 text-red-800', text: 'Failed' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800', text: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    if (key.length <= 8) return '••••••••';
    return `${key.substring(0, 4)}${'•'.repeat(8)}${key.substring(key.length - 4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">API Integrations</h1>
        <p className="text-gray-600">Manage your third-party API integrations</p>
      </div>

      {/* Current Integrations */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Your Integrations</CardTitle>
          <CardDescription>Manage and monitor your connected services</CardDescription>
        </CardHeader>
        <CardContent>
          {integrations.length === 0 ? (
            <Alert>
              <AlertDescription>
                No integrations configured yet. Add your first integration below.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {integrations.map((integration) => {
                const provider = providers.find(p => p.id === integration.provider);
                return (
                  <div key={integration.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{provider?.icon || '🔌'}</div>
                      <div>
                        <h3 className="font-semibold">{provider?.name || integration.provider}</h3>
                        <p className="text-sm text-gray-500">
                          Added {new Date(integration.submitted_at).toLocaleDateString()}
                        </p>
                        {integration.credentials && Object.keys(integration.credentials).length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {Object.entries(integration.credentials).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2">
                                <span className="font-mono">
                                  {showKeys[integration.id] ? value : maskApiKey(value)}
                                </span>
                                <button
                                  onClick={() => setShowKeys(prev => ({...prev, [integration.id]: !prev[integration.id]}))}
                                  className="hover:text-gray-600"
                                >
                                  {showKeys[integration.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(integration.status)}
                      {integration.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTest(integration.id)}
                          disabled={testing}
                        >
                          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(integration.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Providers */}
      <Card>
        <CardHeader>
          <CardTitle>Available Integrations</CardTitle>
          <CardDescription>Connect new services to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((provider) => {
              const isConnected = integrations.some(i => i.provider === provider.id);
              return (
                <Dialog key={provider.id}>
                  <DialogTrigger asChild>
                    <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="text-4xl mb-3">{provider.icon}</div>
                        <h3 className="font-semibold mb-2">{provider.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{provider.description}</p>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">{provider.category}</Badge>
                          {isConnected && (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{provider.icon}</span>
                          {provider.name}
                        </div>
                      </DialogTitle>
                      <DialogDescription>{provider.description}</DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      {provider.required_credentials.map((credKey) => (
                        <div key={credKey}>
                          <Label htmlFor={credKey}>
                            {credKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} *
                          </Label>
                          <Input
                            id={credKey}
                            type="password"
                            required
                            placeholder={`Enter ${credKey}`}
                            value={credentials[credKey] || ''}
                            onChange={(e) => {
                              setCredentials(prev => ({...prev, [credKey]: e.target.value}));
                              setSelectedProvider(provider);
                            }}
                          />
                        </div>
                      ))}

                      {provider.optional_credentials.map((credKey) => (
                        <div key={credKey}>
                          <Label htmlFor={credKey}>
                            {credKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} (Optional)
                          </Label>
                          <Input
                            id={credKey}
                            type="password"
                            placeholder={`Enter ${credKey}`}
                            value={credentials[credKey] || ''}
                            onChange={(e) => {
                              setCredentials(prev => ({...prev, [credKey]: e.target.value}));
                              setSelectedProvider(provider);
                            }}
                          />
                        </div>
                      ))}

                      <Alert>
                        <AlertDescription>
                          <strong>Setup Complexity:</strong> {provider.setup_complexity}
                          <br />
                          <a 
                            href={provider.documentation_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            View Documentation →
                          </a>
                        </AlertDescription>
                      </Alert>

                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1" disabled={submitting}>
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                          Add Integration
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsPage;
